import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';

import {
  BehaviorSubject, catchError, filter, finalize, from, map, Observable, of, Subject, switchMap, takeUntil, tap,
} from 'rxjs';

import { AppUtilityService } from '../app-utility.service';
import { QuoteStoreService } from '../core/rest-services/quote-store.service';
import { UserService } from '../core/services/lmsuser.service';
import { ApiloaderService } from '../shared/loaders/apiloader/apiloader.service';
import { QuoteNavigatorService } from './quotenavigator.service';

const loanStatusRouteMap: Record<string, string> = {
  'New': '/search',
  'Active': '/quote',
  'Submitted': '/fbloanAgreement',
  'NuPay': '/registerLoan',
  'Journal': '/disburseLoan'
};

@Injectable({ providedIn: 'root' })
export class LandingService implements OnDestroy {
  private destroy$ = new Subject<void>();

  private clientListSubject = new BehaviorSubject<any[]>([]);
  readonly clientList$ = this.clientListSubject.asObservable();

  private newClientListSubject = new BehaviorSubject<any[]>([]);
  readonly newClientList$ = this.newClientListSubject.asObservable();

  private complianceListSubject = new BehaviorSubject<any[]>([]);
  readonly complianceList$ = this.complianceListSubject.asObservable();
  private complianceLoadingSubject = new BehaviorSubject<boolean>(false);
  readonly complianceLoading$ = this.complianceLoadingSubject.asObservable();
  private clientListLoadingSubject = new BehaviorSubject<boolean>(false);
  readonly clientListLoading$ = this.clientListLoadingSubject.asObservable();

  private newListLoadingSubject = new BehaviorSubject<boolean>(false);
  readonly newListLoading$ = this.newListLoadingSubject.asObservable();

  private lmsUserSubject = new BehaviorSubject<any | null>(null);
  public readonly lmsUser$ = this.lmsUserSubject.asObservable();

  selectedBranch!: string;
  private cachedProfile: any;

  constructor(
    private quoteStoreService: QuoteStoreService,
    private global_utility: AppUtilityService,
    private apiloaderService: ApiloaderService,
    private quoteNavigator: QuoteNavigatorService,
    private userService: UserService,
    private router: Router
  ) {

    this.userService.onLMSProfileLoaded.pipe(takeUntil(this.destroy$)).subscribe((lmsprofile)=>{
      if(!lmsprofile) {
        if (localStorage.getItem('lms.logout.in.progress') === 'true') {
          console.info('Landing service detected logout in progress; skipping root redirect');
          return;
        }

        document.body.classList.add('no-profile');
        void this.router.navigateByUrl('/login');
        console.warn('No LMS profile loaded');
        return;
      }
      //if((lmsprofile && this.LMSUserProfile != lmsprofile) || this.LMSUserProfile == null) {
        console.log('landing service profile tap: ',lmsprofile);
        this.cachedProfile = lmsprofile;

        this.lmsUserSubject.next(this.cachedProfile);
      //}
    });

    const usrtime = setTimeout(() => {
      if(!this.cachedProfile) {
        this.lmsUserSubject.next(null);
        this.reloadLMSProfile();

        clearTimeout(usrtime);
      }
    }, 500);

    this.userService.UserProfile.pipe(filter(value => value !== null))
    .subscribe(arg => this._lmsProfile = arg);


    // .subscribe((profile)=>{
    //   if(profile && this.lmsUserSubject.getValue() != profile) {
    //     this.lmsUserSubject.next(profile);
    //   }
    // });
  }

    private _lmsProfile : any;


  get LMSUserProfile() {
    return this._lmsProfile;
  }

  getCurrentUser() {
    return this.userService.getLMSProfile();
  }

  getCurrentUserBranchSerial() {
    return this.userService.getLMSProfile()?.delfinBranchSerial;
  }

  reloadLMSProfile() {
    this.lmsUserSubject.next(null);

    this.userService.UserProfile.pipe(tap(profile =>{
      this.cachedProfile = profile;
      this.lmsUserSubject.next(this.cachedProfile);
    })).subscribe();
  }

    // Inside your Angular Service or Component
  updateProfileGeoStatus() {
    if (navigator.geolocation) {
      this.userService.updateProfileGeoStatus();
    }
  }

  private activeClientSubject = new BehaviorSubject<any | null>(null);
  readonly activeClient$ = this.activeClientSubject.asObservable();

  get activeClientCount(){
    return this.activeClient$.pipe(tap(val => {
      console.info('active length: ', val.length ?? 0);
    }),map((x) => Array.of(x).length ?? 0));
  }
  viewClient(quote: any): void {
    this.quoteNavigator.navigateFromNewClientSelection(quote);
    this.activeClientSubject.next(quote);
  }

  private newClientSubject = new BehaviorSubject<any | null>(null);
  readonly newClient$ = this.newClientSubject.asObservable();

  get newClientCount(){
    return Array.of(this.newClientSubject?.getValue() || []).length || 0;
  }
  viewNewClient(quote: any): void {
    if (!quote || !quote.status) {
      console.warn('Invalid quote selection:', quote);
      return;
    }
    console.log('Route from landing service');
    this.quoteNavigator.navigateFromNewClientSelection(quote);
    this.newClientListSubject.next(quote);
  }

  compliance(quote: any): void {
    if (!quote || !quote.status) {
      console.warn('Invalid quote selection:', quote);
      return;
    }
    console.log('Route from landing service');
    this.quoteNavigator.navigateFromNewClientSelection(quote);
    this.newClientListSubject.next(quote);
  }

  viewClients(quote: any): void {
    if (!quote || !quote.status) {
      console.warn('Invalid quote selection:', quote);
      return;
    }
    console.log('Route from landing service');
    this.quoteNavigator.navigateFromNewClientSelection(quote);
    this.newClientListSubject.next(quote);
  }

  findQuotesByBranch(userName: string, branchSerial: string): void {
    this.clientListLoadingSubject.next(true);

    const request$ = () => this.quoteStoreService.getBranchQuotesList({ userName, branchSerial })
      .pipe(tap(data => this.clientListSubject.next(data)));

    request$()
      .pipe(
        catchError(err => this.handleError(err, request$)),
        finalize(() => this.clientListLoadingSubject.next(false)),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }

  findNewByBranch(userName: string, branchSerial: string): void {
    this.newListLoadingSubject.next(true);

    const request$ = () => this.quoteStoreService.getNewClientsList({ userName, branchSerial })
      .pipe(tap(data => this.newClientListSubject.next(data)));

    request$()
      .pipe(
        catchError(err => this.handleError(err, request$)),
        finalize(() => this.newListLoadingSubject.next(false)),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }

  findComplianceByBranch(userName: string, branchSerial: string): void {
    this.complianceLoadingSubject.next(true);

    const request$ = () => this.quoteStoreService.getComplianceList({ userName, branchSerial })
      .pipe(tap(data => this.complianceListSubject.next(data)));

    request$()
      .pipe(
        catchError(err => this.handleError(err, request$)),
        finalize(() => this.complianceLoadingSubject.next(false)),
        takeUntil(this.destroy$)
      ).subscribe();
  }


  private handleError(error: HttpErrorResponse, retryRequest?: () => Observable<any>): Observable<any> {
    if (error.status === 0 || error.status === 502) {
      this.global_utility.error('Network error occurred.', 'API Error');
      this.apiloaderService.setAPIStatus(false);

      if (retryRequest) {
        return from(this.apiloaderService.WaitforOnline())
          .pipe(
            switchMap((waited) => waited ? retryRequest() : of(error)),
            catchError((retryErr) => of(retryErr))
          );
      }
    } else {
      this.global_utility.error(error.error, error.statusText);
    }
    console.error('Error:', error);
    return of(error);
    //return throwError(() => new Error('Please try again later.'));
  }

  setFingerAuth(validFinger: boolean) {
    this.userService.setFingerValid(validFinger);
  }

  get fingerAuth() {
    return this.userService.isFingerAuth;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

}
